import { call, delay, put, takeEvery } from "redux-saga/effects";
import {
  getNewsDataFailed,
  getNewsDataSuccess,
  getAudioSuccess,
  getAudioFailed,
  getVideoSuccess,
  getVideoFailed,
  getAnalyzeArticleSuccess,
  getAnalyzeArticleFailed,
  getGenerateArticleSuccess,
  getGenerateArticleFailed,
  getNewsData,
  resetNews,
  newsLoadComplete,
  getGenerateArticle,
  resetArticle,
  articleLoadComplete,
  reResetArticle,
  getReGenerateArticleSuccess,
  ReArticleLoadComplete,
  getReGenerateArticleFailed,
  getReGenerateArticle,
  getAudioData,
  getVideoData,
  getHeygenData,
  getHeygenDataSuccess,
  getCaptionedNewsReelData,
  getCaptionedNewsReelDataSuccess,
  getCaptionedNewsReelDataFailed,
  getGenerateAnnouncement,
  getGenerateAnnouncementSuccess,
  getGenerateAnnouncementFailed,
  resetAnnouncement,
  announcementLoadComplete,
} from ".";
import { serverIp } from "../../../utils";
import { message } from "antd";

interface FetchResponse {
  body: ReadableStream | null;
}

export function* getData({ payload }) {
  const { articles, gentype, paragraphs, words, token, language } = payload;
  const newsPayload = {
    gentype: gentype,
    paragraphs: paragraphs,
    words: words,
    articles: articles,
    language: language,
  };

  const requestUrl = `${serverIp}/askchatbot`;
  try {
    yield put(resetNews());
    const response: FetchResponse = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newsPayload),
      }),
    );

    if (!response.body) {
      throw new Error("ReadableStream not yet supported in this browser.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    while (true) {
      const { done, value } = yield call(() => reader.read());
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      yield put(getNewsDataSuccess(chunk)); // Dispatch action for each chunk
    }

    yield put(newsLoadComplete());
  } catch (err) {
    yield put(getNewsDataFailed());
  }
}

export function* genAudio({ payload }) {
  const { text, voice, token, language } = payload;
  const audioPayload = {
    text,
    voice,
    ...(language && { language }),
  };
  const requestUrl = `${serverIp}/audiogen`;

  try {
    const response = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(audioPayload),
      }).then((res) => res.json()),
    );

    // Handle direct response from API
    // Backend response structure: { time, gen_type, response (s3_url), doc_id }
    if (response && response.response) {
      yield put(
        getAudioSuccess({
          audio: {
            ...response,
            read: false,
            gen_type: response.gen_type || "audio",
          },
          notification: "Your audio has been generated!",
          gen_type: response.gen_type || "audio",
          generatedTime: new Date().toISOString(),
        }),
      );
      message.success("Your audio has been generated!");
    } else {
      throw new Error("Invalid response from server.");
    }
  } catch (err) {
    yield put(getAudioFailed());
    message.error("Failed to generate audio.");
  }
}

export function* genHeygen({ payload }) {
  const { text, avatar_id, voice_id, token } = payload;

  const heygenPayload = {
    text: text,
    avatar_id: avatar_id,
    voice_id: voice_id,
  };
  const requestUrl = `${serverIp}/heygen_videogen`;

  try {
    const response = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(heygenPayload),
      }),
    );
    const data = yield response.json();
    const taskId = data;

    let taskStatus = "processing";
    let taskResult = null;

    while (taskStatus !== "completed") {
      yield delay(3000); // Poll every 3 seconds

      const statusResponse = yield call(() =>
        fetch(`${serverIp}/heygen_video_status?video_id=${taskId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      );

      const statusData = yield statusResponse.json();
      taskStatus = statusData.status;
      taskResult = statusData;
    }

    // Step 3: Handle task completion
    if (taskStatus === "completed" && taskResult) {
      // Dispatch success action with audio path
      yield put(
        getHeygenDataSuccess({
          audio: {
            ...taskResult,
            read: false,
          },
          notification: "Your video has been generated!",
          gen_type: "video",
          generatedTime: new Date().toISOString(),
        }),
      );
      message.success("Your video has been generated!");
    } else {
      throw new Error("Task failed to complete successfully.");
    }
  } catch (err) {
    yield put(getAudioFailed());
    message.error("Failed to generate video.");
  }
}

export function* genCaptionedNewsReel({ payload }) {
  const { content, language } = payload;

  const startUrl =
    language === "pashto"
      ? "https://merapeshawar.app.n8n.cloud/webhook/pashto-reel"
      : "https://merapeshawar.app.n8n.cloud/webhook/videogen_peshawar";

  const statusUrl = `${serverIp}/caption_status`;

  try {
    const startResponse = yield call(() =>
      fetch(startUrl, {
        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({
          content,

          ...(language && { language }),
        }),
      }).then((res) => res.json()),
    );

    const jobId = startResponse.jobId;

    if (!jobId) {
      throw new Error("Missing jobId from backend");
    }

    let videoUrl = null;

    while (!videoUrl) {
      yield delay(5000);

      const statusResponse = yield call(() =>
        fetch(`${statusUrl}?jobId=${encodeURIComponent(jobId)}`).then((res) =>
          res.json(),
        ),
      );

      if (statusResponse.status === "done") {
        videoUrl = statusResponse.videoUrl;
      } else if (statusResponse.status === "failed") {
        throw new Error(statusResponse.message || "Job failed");
      }
    }

    const cleanVideoUrl = videoUrl?.startsWith("=")
      ? videoUrl.substring(1)
      : videoUrl;

    yield put(
      getCaptionedNewsReelDataSuccess({
        audio: {
          videoUrl: cleanVideoUrl,

          response: cleanVideoUrl,

          read: false,

          gen_type: "video",
        },

        notification: "Your Captioned News Reel has been generated!",

        gen_type: "video",

        generatedTime: new Date().toISOString(),
      }),
    );

    message.success("Your Captioned News Reel has been generated!");
  } catch (err) {
    yield put(getCaptionedNewsReelDataFailed());

    message.error("Failed to generate Captioned News Reel.");
  }
}

//**************** MOCK AUDIO SUCCESS */

// export function* genAudio({ payload }) {
//   const { text, voice, token } = payload;
//   const audioPayload = {
//     text: text,
//     voice: voice,
//   };
//   const requestUrl = `${serverIp}/audiogen`;

//   try {
//     const mockaudio = {
//       audioPath: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
//     };

//     yield put(
//       getAudioSuccess({
//         audio: mockaudio,
//         notification: "Your audio has been generated!",
//         gen_type: "audio",
//         // generatedTime: data.time
//       })
//     );

//     message.success(
//       `Your audio has been generated! Check it here: ${mockaudio.audioPath}`
//     );

//     /*
//     const response = yield call(() =>
//       fetch(requestUrl, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//         body: JSON.stringify(audioPayload),
//       })
//     );
//     const data = yield response.json();
//     */
//   } catch (err) {
//     yield put(getAudioFailed());
//     message.error(`Failed to generate audio.`);
//   }
// }

export function* genVideo({ payload }) {
  const { text } = payload;
  const videoPayload = { content: text };
  const requestUrl = "https://smartforumai.app.n8n.cloud/webhook/news-to-video";

  try {
    const response = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(videoPayload),
      }).then((res) => res.json()),
    );

    // Clean up videoUrl - remove leading '=' if present
    const videoUrl = response.videoUrl?.startsWith("=")
      ? response.videoUrl.substring(1)
      : response.videoUrl;

    yield put(
      getVideoSuccess({
        audio: {
          ...response,
          videoUrl: videoUrl,
          response: videoUrl, // Store in 'response' field for backward compatibility
          read: false,
          gen_type: "video",
        },
        notification: "Your video has been generated!",
        gen_type: "video",
        generatedTime: new Date().toISOString(),
      }),
    );
    message.success("Your video has been generated!");
  } catch (err) {
    yield put(getVideoFailed());
    message.error("Failed to generate video.");
  }
}

export function* analyzeArticles({ payload }: any): any {
  const { article, id, token, language } = payload;
  const newsPayload = {
    article: article,
    language: language,
  };
  const requestUrl = `${serverIp}/analyze`;
  try {
    const data = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newsPayload),
      }),
    );
    const parsedData = yield data.json();
    const payload = { ...parsedData, id };
    yield put(getAnalyzeArticleSuccess(payload));
  } catch (err) {
    yield put(getAnalyzeArticleFailed());
  }
}

export function* generateArticleData({ payload }: any): any {
  const requestUrl = `${serverIp}/generate_article`;
  const { token } = payload;
  const ensureString = (value) => {
    if (Array.isArray(value)) {
      return value[0]?.toString() || "";
    }
    return value?.toString() || "";
  };
  const promptPayload = {
    // description: payload?.description,
    // viewpoints: payload?.keypoints,
    target: payload?.target,
    // length: payload?.length,
    style: payload?.style,
    tone: payload?.tone,
    articles: payload.articles,
    language: payload.language,
  };

  try {
    yield put(resetArticle());
    const response: FetchResponse = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(promptPayload),
      }),
    );

    if (!response.body) {
      throw new Error("ReadableStream not yet supported in this browser.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    while (true) {
      const { done, value } = yield call(() => reader.read());
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      yield put(getGenerateArticleSuccess(chunk)); // Dispatch action for each chunk
    }
    yield put(articleLoadComplete());
  } catch (err) {
    yield put(getGenerateArticleFailed());
  }
}

export function* reGenerateArticleData({ payload }: any): any {
  const requestUrl = `${serverIp}/article_regenerate`;
  const { token } = payload;
  try {
    yield put(reResetArticle());
    const response: FetchResponse = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload.payload),
      }),
    );

    if (!response.body) {
      throw new Error("ReadableStream not yet supported in this browser.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    while (true) {
      const { done, value } = yield call(() => reader.read());
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      yield put(getReGenerateArticleSuccess(chunk)); // Dispatch action for each chunk
    }
    yield put(ReArticleLoadComplete());
  } catch (err) {
    yield put(getReGenerateArticleFailed());
  }
}

export function* generateAnnouncementData({ payload }: any): any {
  const requestUrl = `${serverIp}/annoucement`;
  const { token } = payload;
  try {
    yield put(resetAnnouncement());
    const response: any = yield call(() =>
      fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload.payload),
      }),
    );

    if (!response.ok) {
      const errorData = yield response
        .json()
        .catch(() => ({ message: "An error occurred" }));
      message.error(
        errorData.message ||
          `Failed to generate announcement. Status: ${response.status}`,
      );
      yield put(getGenerateAnnouncementFailed());
      return;
    }

    if (!response.body) {
      throw new Error("ReadableStream not yet supported in this browser.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    while (true) {
      const { done, value } = yield call(() => reader.read());
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      yield put(getGenerateAnnouncementSuccess(chunk)); // Dispatch action for each chunk
    }
    yield put(announcementLoadComplete());
  } catch (err: any) {
    const errorMessage =
      err?.message ||
      "An error occurred while generating the announcement. Please try again.";
    message.error(errorMessage);
    yield put(getGenerateAnnouncementFailed());
  }
}

function* dataSaga() {
  yield takeEvery(getNewsData.type, getData);
  yield takeEvery(getGenerateArticle.type, generateArticleData);
  yield takeEvery(getReGenerateArticle.type, reGenerateArticleData);
  yield takeEvery(getGenerateAnnouncement.type, generateAnnouncementData);
  yield takeEvery(getAudioData.type, genAudio);
  yield takeEvery(getVideoData.type, genVideo);
  yield takeEvery(getHeygenData.type, genHeygen);
  yield takeEvery(getCaptionedNewsReelData.type, genCaptionedNewsReel);
  yield takeEvery("dataApi/getAnalyzeArticle", analyzeArticles);
}
export default dataSaga;
